/**
 * Mirrors GoogleEventsStrategy.js. See EventsTable::findSimpleOutlook() and
 * OutlookEventsProfiles (backend) for the Graph-vs-Google field mapping
 * this file relies on. The recurrence-series parsing helpers below are
 * unchanged from the Google version - OutlookEventsProfiles was built
 * specifically to produce the same RRULE-string-array shape in
 * event_profile.data.recurrence, so none of that logic needed to change.
 */
import EventsStrategy from "./EventsStrategy";
import AxiosConfig from "../../AxiosConfig";
import { RRule, RRuleSet } from "rrule";
import { lcFirst } from "../../helpers/string";
import { cleanRRule } from "../../helpers/calendar";
import { getLinksFromDescription, linkify } from "../../helpers/widgetItems";

const parseISO8601Basic = (inputStr) => {
  const input = inputStr.trim();

  if (input.length === 8) {
    const [y, m, d] = [input.slice(0, 4), input.slice(4, 6), input.slice(6, 8)];
    return new Date(`${y}-${m}-${d}T00:00:00Z`);
  }

  // 15 chars without Z, or 16 with Z: YYYYMMDDTHHmmss[Z]
  const bare = input.replace(/Z$/, "");
  if (bare.length === 15 && bare[8] === "T") {
    const y = bare.slice(0, 4);
    const mo = bare.slice(4, 6);
    const d = bare.slice(6, 8);
    const hh = bare.slice(9, 11);
    const mm = bare.slice(11, 13);
    const ss = bare.slice(13, 15);
    // Return as UTC — caller must ensure dtstart is also UTC
    return new Date(`${y}-${mo}-${d}T${hh}:${mm}:${ss}Z`);
  }

  return new Date(NaN);
};
const sanitizeValue = (value, defaultValue) => {
  return value && !Array.isArray(value) ? value : defaultValue;
};

const isSearchableLocationString = (locationName) => {
  if (!locationName) {
    return false;
  }

  const lower = locationName.toLowerCase();

  const omitWords = ["http", "www", "tba"];
  if (omitWords.some((word) => lower.includes(word))) {
    return false;
  }

  const commasCount = (locationName.match(/,/g) || []).length;
  if (commasCount < 2 || commasCount > 5) {
    return false;
  }

  return true;
};

const parseRuleDates = (rrule, rule) => {
  if (!rrule) {
    return [];
  }
  const dates = [];

  for (const ruleLine of rrule) {
    const regex = new RegExp(rule);
    if (regex.test(ruleLine)) {
      let cleaned = ruleLine;

      // Handle TZID format: EXDATE;TZID=Europe/Athens:20260306T143000
      cleaned = cleaned.replace(
        /;TZID=[^:]+:(\d{8}T\d{6})/g,
        (_, group1) => `${group1}Z`
      );

      // Handle VALUE=DATE format: EXDATE;VALUE=DATE:20260207
      cleaned = cleaned.replace(/;VALUE=DATE:(\d{8})/g, (_, group1) => group1);

      // Strip the rule prefix (EXDATE or RDATE)
      cleaned = cleaned.replace(rule, "");

      dates.push(cleaned);
    }
  }

  return dates;
};

/**
 *
 * @param rrule
 * @returns {Date[]|null}
 */
const parseRecurrenceSeries = (startDate, rrule) => {
  if (!rrule) {
    return [null, null];
  }

  const exDates = parseRuleDates(rrule, "EXDATE");
  const rdates = parseRuleDates(rrule, "RDATE");
  const cleanRuleLines = rrule.filter((i) => !/(EXDATE|RDATE)/.test(i));

  if (!cleanRuleLines.length) {
    return [null, null];
  }

  try {
    const options = RRule.parseString(cleanRuleLines.join("\n"));
    options.dtstart = new Date(startDate);

    const rule = cleanRRule(options);
    const rruleSet = new RRuleSet();
    rruleSet.rrule(rule);

    rruleSet.rrule(rule);

    const dtStartTime = options.dtstart.toISOString().slice(11); // "HH:MM:SS.sssZ"

    const toDateKey = (d) => {
      // Parse just the date portion in UTC from the ISO8601Basic string
      const iso = parseISO8601Basic(d);
      return iso.toISOString().slice(0, 10);
    };

    const buildAtTime = (dateKey) => new Date(`${dateKey}T${dtStartTime}`);

    // Get rrule occurrences indexed by date
    const allExAndRDates = [...exDates, ...rdates].map(parseISO8601Basic);

    const occurrencesByDate = new Map();

    if (allExAndRDates.length) {
      // Only look up occurrences around the dates we actually need to match
      for (const targetDate of allExAndRDates) {
        const from = new Date(targetDate);
        from.setDate(from.getDate() - 1);
        const to = new Date(targetDate);
        to.setDate(to.getDate() + 1);

        for (const occ of rruleSet.between(from, to, true)) {
          occurrencesByDate.set(occ.toISOString().slice(0, 10), occ);
        }
      }
    }

    for (const exDate of exDates) {
      const dateKey = toDateKey(exDate);
      const occurrence = occurrencesByDate.get(dateKey);
      if (occurrence) {
        rruleSet.exdate(occurrence); // exact same object = guaranteed valueOf match
      }
    }

    for (const rdate of rdates) {
      const dateKey = toDateKey(rdate);
      rruleSet.rdate(buildAtTime(dateKey));
    }

    for (const rdate of rdates) {
      const d = parseISO8601Basic(rdate);
      d.setUTCHours(
        options.dtstart.getUTCHours(),
        options.dtstart.getUTCMinutes(),
        options.dtstart.getUTCSeconds(),
        0
      );
      rruleSet.rdate(d);
    }

    return [rruleSet.toString(), rule.toText()];
  } catch (e) {
    console.error(e);
    return [null, null];
  }
};

class OutlookEventsStrategy extends EventsStrategy {
  processData(data, integration, modelData) {
    return data.map((i) => {
      const event = i.data;

      const startDate = i.start_date;
      const endDate = i.end_date;

      // Graph's exception events carry `originalStart` as a flat ISO
      // dateTime string (unlike Google's originalStartTime object split
      // into .date/.dateTime) - no branching needed here.
      const eventProfileStartDate = event?.originalStart || null;

      // No OutlookColors-equivalent sync exists yet - always null for now.
      const eventColor = null;

      const coverImagePath =
        i.attachments && i.attachments.length
          ? i.attachments[0].data.path
          : null;

      const coverImage = coverImagePath
        ? coverImagePath.includes("http")
          ? coverImagePath
          : AxiosConfig.getEndpointAddress() + `/${coverImagePath}`
        : null;

      const oldRule = event?.recurrence;
      const rruleFromEventProfile = i?.event_profile?.data?.recurrence;
      const rrule = rruleFromEventProfile || oldRule;

      const [recurrence_series, rruleDesc] = parseRecurrenceSeries(
        eventProfileStartDate,
        rrule
      );

      // Graph exposes this directly as a boolean, unlike Google's
      // date-vs-dateTime presence check.
      const isAllDayEvent = !!event.isAllDay;

      // body.content is already HTML - Outlook auto-converts typed URLs
      // into real <a> tags itself, so running it through linkify() (built
      // for plain text) risks double-wrapping or mangling existing
      // markup. Only fall back through linkify() when there's no rich
      // body, since bodyPreview is plain text.
      // NEEDS VERIFICATION against the actual linkify()/
      // getLinksFromDescription() implementations - not tested against
      // real HTML input.
      const rawBody = event?.body?.content || "";
      const description = rawBody ? rawBody : linkify(event?.bodyPreview || "");

      const descriptionLinks = getLinksFromDescription(description);

      const tagsData = Object.values(i.data?.tags || []);

      const tags = tagsData.map((tag) => ({
        label: tag,
      }));

      // Graph nests organizer contact info one level deeper than Google.
      const organizerInfo = event.organizer?.emailAddress || {};
      const organizer = {
        name: sanitizeValue(
          organizerInfo.name ? organizerInfo.name : organizerInfo.address,
          null
        ),
        email: sanitizeValue(organizerInfo.address, null),
      };

      const ctas = {
        linkFirst: descriptionLinks[0] || null,
        linkLast: descriptionLinks[1] || null,
        eventPage: null,
        form: null,
        organizer:
          organizer && organizer.email
            ? {
                url: `mailto:${organizer.email}`,
                label: "Contact organizer",
              }
            : null,
      };

      // event.location is an object in Graph ({displayName, address, ...}),
      // not a plain string like Google's.
      const locationName = event.location?.displayName || "";

      // No OutlookPlaces-equivalent sync exists yet - always null for now,
      // same as Google's code before GooglePlaces existed. This naturally
      // starts returning real data once such a sync is added, since
      // OutlookEvents already computes db_relations.place_id the same way
      // GoogleEvents does.
      const eventPlace =
        i?.place?.data && i.place.data.name?.startsWith("places/")
          ? i.place.data
          : null;

      const place = eventPlace
        ? {
            displayName: eventPlace.displayName?.text || "",
            summary: eventPlace.editorialSummary?.text || "",
            iconBgColor: eventPlace.iconBackgroundColor,
            iconURi: eventPlace.iconMaskBaseUri,
            rating: eventPlace.rating,
            userRatingCount: eventPlace.userRatingCount,
          }
        : null;

      if (place && eventPlace?.photos) {
        place.photos = eventPlace.photos.map((photo) => {
          return {
            url: photo?.path.includes("http")
              ? photo.path
              : AxiosConfig.getEndpointAddress() + `/${photo.path}`,
            caption: photo?.authorAttributions[0]?.displayName,
          };
        });
      }

      return {
        id: i.id,
        ics_id: i.platform_event_id,
        slug: i.short_slug,
        description,
        name: event.subject,
        start_time: startDate,
        end_time: endDate,
        allDay: isAllDayEvent,
        timezone: "UTC",
        recurrence_description: rruleDesc ? lcFirst(rruleDesc) : null,
        recurrence_series,
        display_location: locationName,
        cover_image: coverImage,
        location: {
          city: " ",
          name: locationName,
          hasSearchableLocation: eventPlace
            ? true
            : isSearchableLocationString(locationName),
        },
        organizer,
        contact: {
          phone: "",
        },
        // Graph's calendar resource only has `hexColor` (closest analog
        // to Google's backgroundColor) - there's no paired foreground/
        // font color the way Google's calendar exposes both. fontColor
        // is left null here; the widget should apply a sensible default
        // or compute a contrasting color from accentColor if needed.
        accentColor: eventColor
          ? eventColor.background
          : i.calendar?.data?.hexColor || null,
        fontColor: eventColor ? eventColor.foreground : null,
        ctas,
        place,
        is_featured: i.is_featured,
        tags,
      };
    });
  }
}

export default OutlookEventsStrategy;
